import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormArray } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressBarModule } from 'primeng/progressbar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { EditorModule } from 'primeng/editor';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';

import { FileService } from '../../../core/services/file';
import { UserService } from '../../../core/services/user.service';
import { EncryptionService } from '../../../core/services/encryption';
import { CustomerService } from '../../../core/services/customer.service';
import { SubscriberService } from '../../../core/services/subscriber.service';
import { CreateCustomerRequest, DecisionMaker } from '../../../core/models/customer.model';
import { SETTINGS } from '../../../core/config/common.settings';

@Component({
  selector: 'app-add-edit-customer',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    PasswordModule,
    ToastModule,
    ProgressBarModule,
    RadioButtonModule,
    EditorModule,
    LoadingSpinnerComponent
  ],
  providers: [MessageService],
  templateUrl: './add-edit-customer.html',
  styleUrl: './add-edit-customer.scss'
})
export class AddEditCustomer implements OnInit {
  @Input() isDrawerMode: boolean = false;
  @Input() prefilledData: any = null;
  @Output() customerCreated = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  customerForm: FormGroup;
  industryOptions: any[];
  numberOfEmployeesOptions: any[];
  salutationOptions: any[];
  interestLevelOptions: any[];
  customerOriginOptions: { label: string; value: string }[];

  // Edit mode properties
  isEditMode: boolean = false;
  customerId: string | null = null;
  existingCustomer: any = null;

  isFileUploading = false;
  uploadedFileUrl: string | null = null;
  uploadedFile: File | null = null;
  dragOver: boolean = false;
  isSubmitting = false;
  isLoading: boolean = false;

  // Current user info
  loggedInUserSubscriberId: string = '';

  // Navigation context
  private previousUrl: string = '';
  private referrerUrl: string = '';

  constructor(private fb: FormBuilder,
    private http: HttpClient,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private fileService: FileService,
    private userService: UserService,
    private encryption: EncryptionService,
    private customerService: CustomerService,
    private subscriberService: SubscriberService
  ) {
    this.customerForm = this.fb.group({
      businessName: ['', Validators.required],
      zipCode: ['', Validators.required],
      industry: ['', Validators.required],
      numberOfEmployees: ['', Validators.required],
      decisionMakers: this.fb.array([]),
      interestLevel: ['', Validators.required],
      acceptedPromotionalGifts: ['no', Validators.required],
      customerOrigin: ['', Validators.required],
      notes: [''],
      documents: ['']
    });

    // Add initial decision maker
    this.addDecisionMaker();

    // Initialize industry options as empty array - will be loaded from subscriber configuration
    this.industryOptions = [];
    this.numberOfEmployeesOptions = [
      { label: '0-1', value: '0-1' },
      { label: '2-12', value: '2-12' },
      { label: '13-30', value: '13-30' },
      { label: 'More than 30', value: 'More than 30' },
      { label: 'Unknown', value: 'Unknown' },
    ];
    this.salutationOptions = [
      { label: 'Mr.', value: 'Mr.' },
      { label: 'Mrs.', value: 'Mrs.' },
      { label: 'Unknown', value: 'Unknown' },
    ];
    this.interestLevelOptions = [
      { label: 'Not evident', value: 'Not evident' },
      { label: 'Low', value: 'Low' },
      { label: 'Medium', value: 'Medium' },
      { label: 'High', value: 'High' },
    ];
    this.customerOriginOptions = [
      { label: 'Online Search', value: 'Online Search' },
      { label: 'Social Media', value: 'Social Media' },
      { label: 'Email', value: 'Email' },
      { label: 'Recommendation', value: 'Recommendation' },
      { label: 'Letter', value: 'Letter' },
      { label: 'Event/Trade Show', value: 'Event/Trade Show' },
      { label: 'Cold Call', value: 'Cold Call' },
    ];
  }

  ngOnInit() {
    this.loadCurrentUser();
    this.loadIndustries();
    this.checkEditMode();
    this.handlePrefilledData();
    this.trackNavigationContext();
  }

  private trackNavigationContext(): void {
    // Get referrer from browser history
    this.referrerUrl = document.referrer;
    
    // Track router navigation history
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = event.url;
      }
    });
  }

  private getRedirectDestination(): string {
    // Check if we have a specific return URL in query params
    const returnUrl = this.route.snapshot.queryParams['returnUrl'];
    if (returnUrl) {
      return returnUrl;
    }

    // Check if we came from a customer view page
    if (this.previousUrl && this.previousUrl.includes('/admin/view-customer/')) {
      return this.previousUrl;
    }

    // Check if we came from customer table
    if (this.previousUrl && this.previousUrl.includes('/admin/customers')) {
      return '/admin/customers';
    }

    // Check referrer URL for context
    if (this.referrerUrl) {
      if (this.referrerUrl.includes('/admin/view-customer/')) {
        return this.referrerUrl;
      }
      if (this.referrerUrl.includes('/admin/customers')) {
        return '/admin/customers';
      }
    }

    // Default fallback - go to customer table
    return '/admin/customers';
  }

  private loadCurrentUser(): void {
    const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    if (encryptedUserStr) {
      const userStr = this.encryption.decrypt(encryptedUserStr);
      const user = JSON.parse(userStr);
      this.loggedInUserSubscriberId = typeof user.subscriberId === 'object' ? user.subscriberId?._id : user.subscriberId;
      
    }
  }

  private loadIndustries(): void {
    const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    
    if (encryptedUserStr) {
      const userStr = this.encryption.decrypt(encryptedUserStr);
      const user = JSON.parse(userStr);
      
      const subscriberId = user.subscriberId?._id || user.subscriberId;
      
      if (subscriberId) {
        this.subscriberService.findSubscriber(subscriberId).then((subscriber: any) => {
          // Check if subscriber is wrapped in a response object
          let actualSubscriber = subscriber;
          if (subscriber && subscriber.subscriber) {
            actualSubscriber = subscriber.subscriber;
          }
          
          if (actualSubscriber && actualSubscriber.industries && actualSubscriber.industries.length > 0) {
            this.industryOptions = actualSubscriber.industries.map((industry: string) => ({
              label: industry,
              value: industry
            }));
          } else {
            // Show error message if no industries are configured
            this.messageService.add({
              severity: 'error',
              summary: 'Configuration Error',
              detail: 'No industries are configured for your company. Please contact your administrator to set up industry options.'
            });
            this.industryOptions = [];
          }
        }).catch((error: any) => {
          console.error('Error loading industries:', error);
          // Show error message if subscriber data fails to load
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load industry options. Please refresh the page or contact support if the issue persists.'
          });
          this.industryOptions = [];
        });
      } else {
        // Show error message if no subscriber ID
        this.messageService.add({
          severity: 'error',
          summary: 'Configuration Error',
          detail: 'Unable to determine your company configuration. Please contact your administrator.'
        });
        this.industryOptions = [];
      }
    } else {
      // Show error message if no user data
      this.messageService.add({
        severity: 'error',
        summary: 'Authentication Error',
        detail: 'Unable to load user information. Please log in again.'
      });
      this.industryOptions = [];
    }
  }

  private checkEditMode() {
    // Check if we're in edit mode from query parameters
    this.route.queryParams.subscribe(params => {
      if (params['mode'] === 'edit' && params['id']) {
        this.isEditMode = true;
        this.customerId = params['id'];
        this.loadCustomerForEdit();
      }
    });
  }

  private loadCustomerForEdit() {
    if (!this.customerId) return;

    this.isLoading = true;
    this.customerService.getCustomerById(this.customerId).subscribe({
      next: (response: any) => {
        if (response && response.customer) {
          this.existingCustomer = response.customer;
          this.populateFormWithCustomerData(response.customer);
          this.isLoading = false;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Customer not found'
          });
          this.router.navigate(['/admin/customers']);
        }
      },
      error: (error) => {
        console.error('Error loading customer:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load customer details'
        });
        this.router.navigate(['/admin/customers']);
      }
    });
  }

  private handlePrefilledData(): void {
    if (this.isDrawerMode && this.prefilledData) {
      // Auto-fill business name from lookup result
      let businessName = this.prefilledData.businessName || '';
      
      // If coming from manual geofence and business name is "location", clear it
      if (this.prefilledData.lookupSource === 'MANUAL_GEOFENCE' && 
          businessName.toLowerCase().trim() === 'location') {
        businessName = '';
      }
      
      this.customerForm.patchValue({
        businessName: businessName
      });

      // Store additional data for future use
      if (this.prefilledData.placeId) {
        console.log('Google Place ID:', this.prefilledData.placeId);
      }
      
      if (this.prefilledData.coordinates) {
        console.log('Coordinates:', this.prefilledData.coordinates);
      }
    }
  }

  private populateFormWithCustomerData(customer: any) {

    this.decisionMakers.clear();

    this.customerForm.patchValue({
      businessName: customer.businessName,
      zipCode: customer.zipCode,
      industry: customer.industry,
      numberOfEmployees: customer.numberOfEmployees,
      interestLevel: customer.interestLevel,
      acceptedPromotionalGifts: customer.acceptedPromotionalGifts ? 'yes' : 'no',
      customerOrigin: customer.customerOrigin || '',
      notes: customer.notes || '',
      documents: customer.documents || ''
    });

    if (customer.documents) {
      this.uploadedFileUrl = customer.documents;
    }

    if (customer.decisionMaker && customer.decisionMaker.length > 0) {
      customer.decisionMaker.forEach((dm: any) => {
        this.addDecisionMaker();
        const lastIndex = this.decisionMakers.length - 1;
        this.decisionMakers.at(lastIndex).patchValue({
          salutation: dm.salutation,
          firstName: dm.firstName,
          lastName: dm.lastName,
          email: dm.email,
          mobileNumber: dm.mobileNumber
        });
      });
    } else {
      this.addDecisionMaker();
    }
  }

  get decisionMakers() {
    return this.customerForm.get('decisionMakers') as FormArray;
  }

  addDecisionMaker() {
    const decisionMakerGroup = this.fb.group({
      salutation: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      mobileNumber: ['', Validators.required]
    });

    this.decisionMakers.push(decisionMakerGroup);
  }

  removeDecisionMaker(index: number) {
    if (this.decisionMakers.length > 1) {
      this.decisionMakers.removeAt(index);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'At least one decision maker is required.'
      });
    }
  }

  getDecisionMakerFormGroup(index: number): FormGroup {
    return this.decisionMakers.at(index) as FormGroup;
  }

  getDecisionMakerFieldError(index: number, fieldName: string): string | null {
    const decisionMakerGroup = this.getDecisionMakerFormGroup(index);
    const control = decisionMakerGroup.get(fieldName);
    if (control && control.invalid && control.touched) {
      if (control.errors?.['required']) {
        return 'This field is required';
      }
      if (control.errors?.['email']) {
        return 'Please enter a valid email address';
      }
    }
    return null;
  }

  getFieldError(fieldName: string): string | null {
    const control = this.customerForm.get(fieldName);
    if (control && control.invalid && control.touched) {
      if (control.errors?.['required']) {
        return 'This field is required';
      }
      if (control.errors?.['email']) {
        return 'Please enter a valid email address';
      }
    }
    return null;
  }

  onSubmit(): void {
    if (this.customerForm.valid) {
      this.isSubmitting = true;
      const formData = this.customerForm.value;

      const acceptedPromotionalGifts = formData.acceptedPromotionalGifts === 'yes';

      const customerData: CreateCustomerRequest = {
        businessName: formData.businessName,
        zipCode: formData.zipCode,
        industry: formData.industry,
        numberOfEmployees: formData.numberOfEmployees,
        decisionMaker: formData.decisionMakers.map((dm: DecisionMaker) => ({
          salutation: dm.salutation,
          firstName: dm.firstName,
          lastName: dm.lastName,
          email: dm.email,
          mobileNumber: dm.mobileNumber
        })),
        interestLevel: formData.interestLevel,
        acceptedPromotionalGifts: acceptedPromotionalGifts,
        customerOrigin: formData.customerOrigin,
        notes: formData.notes,
        documents: this.uploadedFileUrl || formData.documents,
        subscriberId: this.loggedInUserSubscriberId,
        googlePlaceId: this.prefilledData?.placeId || undefined,
        coordinates: this.prefilledData?.coordinates || undefined,
        formattedAddress: this.prefilledData?.formattedAddress || undefined
      };


      if (this.isEditMode && this.customerId) {
        this.customerService.updateCustomer(this.customerId, customerData).subscribe({
          next: (response) => {
            this.isSubmitting = false;
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Customer updated successfully!'
            });

            setTimeout(() => {
              const redirectDestination = this.getRedirectDestination();
              this.router.navigateByUrl(redirectDestination);
            }, 2000);
          },
          error: (error) => {
            this.isSubmitting = false;
            console.error('Error updating customer:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.error?.message || 'Failed to update customer. Please try again.'
            });
          }
        });
      } else {
        this.customerService.createCustomer(customerData).subscribe({
          next: (response) => {
            this.isSubmitting = false;
            
            if (this.isDrawerMode) {
              // Emit success event for drawer mode
              this.customerCreated.emit({
                customerId: response.customer?._id || response._id,
                businessName: customerData.businessName,
                ...response
              });
            } else {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Customer created successfully!'
              });

              this.customerForm.reset({
                acceptedPromotionalGifts: 'no',
                customerOrigin: '',
                notes: ''
              });
              this.decisionMakers.clear();
              this.addDecisionMaker();
              this.uploadedFileUrl = null;
              this.uploadedFile = null;

              setTimeout(() => {
                this.router.navigate(['/admin/view-customer', response.customer?._id || response._id]);
              }, 2000);
            }
          },
          error: (error) => {
            this.isSubmitting = false;
            console.error('Error creating customer:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.error?.message || 'Failed to create customer. Please try again.'
            });
          }
        });
      }
    } else {
      Object.keys(this.customerForm.controls).forEach(key => {
        const control = this.customerForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });

      for (let i = 0; i < this.decisionMakers.length; i++) {
        const decisionMakerGroup = this.getDecisionMakerFormGroup(i);
        Object.keys(decisionMakerGroup.controls).forEach(key => {
          const control = decisionMakerGroup.get(key);
          if (control) {
            control.markAsTouched();
          }
        });
      }

      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly.'
      });
    }
  }

  private uploadImage(file: File): void {
    this.isFileUploading = true;

    this.uploadedFile = file;

    this.fileService.postFile(file, "USER_IMAGE_UPLOAD").subscribe({
      next: (res) => {
        console.log('UserAccountSetup: Upload successful', res);
        this.isFileUploading = false;
        const data = res as any;

        this.uploadedFileUrl = data.url;

        this.customerForm.patchValue({
          documents: data.url
        });

        this.fileService.fileUploadingSubject.next({
          type: "USER_IMAGE_UPLOAD",
          loading: false,
        });

        this.messageService.add({
          severity: 'success',
          summary: 'File uploaded successfully',
          detail: `${file.name} has been uploaded`
        });
      },
      error: (error) => {
        console.error('UserAccountSetup: Upload error', error);
        this.isFileUploading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Upload failed',
          detail: error.error?.message || `Failed to upload ${file.name}`
        });
      }
    });
  }

  removeUploadedFile(): void {
    this.uploadedFileUrl = null;
    this.uploadedFile = null;

    this.customerForm.patchValue({
      documents: ''
    });

    const fileInput = document.getElementById('file-input-idCard') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

    this.messageService.add({
      severity: 'info',
      summary: 'File removed',
      detail: 'File has been removed'
    });
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  // Helper method to get the maximum file size (10MB for customer documents)
  private getMaxFileSize(): number {
    return 10 * 1024 * 1024; // 10MB in bytes
  }

  // Helper method to format file size (similar to drawer component)
  formatSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private validateFile(file: File): boolean {
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const maxSize = this.getMaxFileSize();

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid file type',
        detail: 'Please select a valid file type: PDF, PNG, JPG'
      });
      return false;
    }

    // Check file size
    if (file.size > maxSize) {
      this.messageService.add({
        severity: 'error',
        summary: 'File too large',
        detail: `File size must be less than ${maxSize / 1000000}MB`
      });
      return false;
    }

    return true;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    
    // Validate file before uploading
    if (!this.validateFile(file)) {
      return;
    }

    this.uploadImage(file);
  }

  triggerFileInput(): void {
    if (this.isFileUploading) return;

    const fileInput = document.getElementById('file-input-idCard') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileUpload(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validate file before uploading
      if (!this.validateFile(file)) {
        return;
      }
      
      this.uploadImage(file);
    }
  }

  isFormModified(): boolean {
    if (!this.isEditMode || !this.existingCustomer) return false;

    const currentValues = this.customerForm.value;
    const originalValues: { [key: string]: any } = {
      businessName: this.existingCustomer.businessName,
      businessId: this.existingCustomer.businessId,
      zipCode: this.existingCustomer.zipCode,
      industry: this.existingCustomer.industry,
      numberOfEmployees: this.existingCustomer.numberOfEmployees,
      interestLevel: this.existingCustomer.interestLevel,
      acceptedPromotionalGifts: this.existingCustomer.acceptedPromotionalGifts ? 'yes' : 'no',
      customerOrigin: this.existingCustomer.customerOrigin || '',
      notes: this.existingCustomer.notes || '',
      documents: this.existingCustomer.documents || ''
    };


    for (const key in originalValues) {
      if (currentValues[key as keyof typeof currentValues] !== originalValues[key]) {
        return true;
      }
    }


    const currentDecisionMakers = currentValues.decisionMakers;
    const originalDecisionMakers = this.existingCustomer.decisionMaker || [];

    if (currentDecisionMakers.length !== originalDecisionMakers.length) {
      return true;
    }

    for (let i = 0; i < currentDecisionMakers.length; i++) {
      const current = currentDecisionMakers[i];
      const original = originalDecisionMakers[i];
      if (!original) return true;

      if (current.salutation !== original.salutation ||
        current.firstName !== original.firstName ||
        current.lastName !== original.lastName ||
        current.email !== original.email ||
        current.mobileNumber !== original.mobileNumber) {
        return true;
      }
    }

    return false;
  }


  onNavigateAway(): void {
    if (this.isFormModified()) {

      console.log('Form has been modified');
    }
  }
}
